// ✅ src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';

const routes = [
  {
    path: '/',
    name: 'booking-engine',
    component: () => import('@/views/BookingMain.vue'),
  },
  {
    path: '/activities/',
    name: 'tours-booking-engine',
    component: () => import('@/views/ToursBookingMain.vue'),
  },
  {
    path: '/landing/:slug',
    name: 'landing-page',
    component: () => import('@/views/LandingPage.vue'),
  },
  {
    path: '/client-IDTag/:uuid',
    name: 'client-photo',
    component: () => import('@/views/client-image-upload/ClientPhotoUpload.vue'),
    props: true,
  },
  {
    path: '/passports/',
    name: 'passport-purchase',
    component: () => import('@/views/passports/PassportPurchaseView.vue'),
  },

  // ✅ TOOLS (lead magnets)
  {
    path: '/:magnetSlug/',
    name: 'tool-lead-magnet',
    component: () => import('@/views/tools/LeadMagnetRunner.vue'),
    props: true,
  },
  {
    path: '/:magnetSlug/resultado/',
    name: 'tool-lead-magnet-result',
    component: () => import('@/views/tools/LeadMagnetResult.vue'),
    props: true,
  },
  {
    path: '/:magnetSlug/final/',
    name: 'tool-lead-magnet-finish',
    component: () => import('@/views/tools/LeadMagnetFinish.vue'),
    props: true,
  },

  {
    path: '/landing-not-found',
    name: 'landing-not-found',
    component: () => import('@/views/LandingNotFound.vue'),
  },
  {
    path: '/company-inactive',
    name: 'company-inactive',
    component: () => import('@/views/CompanyInactive.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFound.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  let subdomain;

  if (process.env.VUE_APP_ENVIROMENT === 'dev') {
    // subdomain = 'tools';
    subdomain = 'traveldesk';
  } else {
    const hostname = window.location.hostname;
    subdomain = hostname.split('.')[0];
  }

  const isToolsSubdomain = subdomain === 'tools';

  // ✅ Si estás en tools.traveldesk.mx, NO cargues companyData ni valides servicios.
  // Solo permitimos rutas de tools + not-found
  if (isToolsSubdomain) {
    const TOOLS_ROUTES = new Set([
      'tool-lead-magnet',
      'tool-lead-magnet-result',
      'tool-lead-magnet-finish',
      'not-found',
    ]);

    if (!TOOLS_ROUTES.has(to.name)) {
      // Evita que entren a / o /activities en tools
      return next({ name: 'not-found' });
    }

    return next();
  }

  // Rutas públicas que deben pasar sin bloqueos de servicios
  const PUBLIC_ROUTES = new Set([
    'not-found',
    'company-inactive',
    'landing-not-found',
    'client-photo',
    'passport-purchase',
  ]);

  if (PUBLIC_ROUTES.has(to.name)) {
    return next();
  }

  // Carga de company por subdominio (necesaria para header/colores/contacto)
  try {
    await store.dispatch('fetchCompanyData', subdomain);
  } catch (e) {
    console.log('COMPANY DATA FETCH ERROR', e);
  }

  const companyData = store.getters.getCompanyData;
  if (!companyData) {
    console.log('COMPANY DATA NOT FOUND');
    return next({ name: 'not-found' });
  }

  // Rutas tipo landing que tienen su propia lógica de carga
  if (to.name === 'landing-page') {
    const slug = to.params.slug;
    const companyId = companyData.data.company_id;

    await store.dispatch('fetchLandingPageData', { slug, companyId });
    const landingData = store.getters.getLandingPageData;

    if (!landingData) {
      return next({ name: 'landing-not-found' });
    }
    return next();
  }

  // client-photo
  if (to.name === 'client-photo') {
    return next();
  }

  // Estado compañía
  const companyStatus = companyData.data.company_status;

  if (companyStatus === 4) {
    return next({ name: 'company-inactive' });
  } else if (companyStatus > 1 && companyStatus < 4) {
    console.log('COMPANY DATA NOT FOUND ON STATUS');
    return next({ name: 'not-found' });
  }

  // Servicios activos: puede venir como string JSON o array
  let activeServices = companyData.data.company_active_services ?? [];
  if (typeof activeServices === 'string') {
    try {
      activeServices = JSON.parse(activeServices);
    } catch {
      activeServices = [];
    }
  }
  if (!Array.isArray(activeServices)) activeServices = [];

  // Redirecciones según servicios
  if (activeServices.length === 1) {
    if (activeServices.includes(1)) {
      // Solo shuttle
      if (to.name === 'tours-booking-engine') return next({ name: 'not-found' });
      return next();
    } else if (activeServices.includes(2)) {
      // Solo tours
      if (to.name === 'booking-engine') return next({ name: 'tours-booking-engine' });
      return next();
    }
  } else if (activeServices.length === 2) {
    // Ambos activos
    if (to.name === 'tours-booking-engine' || to.name === 'booking-engine') {
      return next();
    }
    return next({ name: 'booking-engine' });
  } else {
    return next({ name: 'not-found' });
  }
});

export default router;
